import {
  INCOME_TAX_BANDS,
  SOCIAL_INSURANCE,
  GESY,
  EMPLOYER_EXTRA,
} from "./TaxConfig";

/** Annual income tax on a given annual taxable amount, using Cyprus' progressive bands. */
export function calculateAnnualIncomeTax(annualTaxable) {
  if (!annualTaxable || annualTaxable <= 0) return 0;

  let remaining = annualTaxable;
  let previousCap = 0;
  let tax = 0;

  for (const band of INCOME_TAX_BANDS) {
    if (remaining <= 0) break;
    const bandSize = band.upTo - previousCap;
    const taxableInBand = Math.min(remaining, bandSize);
    tax += taxableInBand * band.rate;
    remaining -= taxableInBand;
    previousCap = band.upTo;
  }

  return tax;
}

/** Employee/employer Social Insurance for one pay period, respecting the annual ceiling. */
export function calculateSocialInsurance(periodGross, ytdInsurable = 0) {
  const remainingCap = Math.max(SOCIAL_INSURANCE.annualCeiling - ytdInsurable, 0);
  const insurableThisPeriod = Math.min(periodGross, remainingCap);

  return {
    employee: insurableThisPeriod * SOCIAL_INSURANCE.employeeRate,
    employer: insurableThisPeriod * SOCIAL_INSURANCE.employerRate,
    insurableAmount: insurableThisPeriod,
  };
}

/** Employee/employer GESY for one pay period, respecting the annual ceiling. */
export function calculateGESY(periodGross, ytdGesyable = 0) {
  const remainingCap = Math.max(GESY.annualCeiling - ytdGesyable, 0);
  const gesyableThisPeriod = Math.min(periodGross, remainingCap);

  return {
    employee: gesyableThisPeriod * GESY.employeeRate,
    employer: gesyableThisPeriod * GESY.employerRate,
    gesyableAmount: gesyableThisPeriod,
  };
}

/** Employer-only extras. Redundancy/HRDF follow the SI ceiling; Social Cohesion Fund is uncapped. */
export function calculateEmployerExtras(periodGross, ytdInsurable = 0) {
  const remainingSICap = Math.max(SOCIAL_INSURANCE.annualCeiling - ytdInsurable, 0);
  const cappedBase = Math.min(periodGross, remainingSICap);

  return {
    redundancyFund: cappedBase * EMPLOYER_EXTRA.redundancyFund,
    humanResourceDevelopment: cappedBase * EMPLOYER_EXTRA.humanResourceDevelopment,
    socialCohesionFund: periodGross * EMPLOYER_EXTRA.socialCohesionFund,
  };
}

export function calculateContributions(){
  return(
    <div>calculate Contributions</div>
  )
}

/**
 * Master calculation — full payslip breakdown for one pay period.
 *
 * @param {Object} params
 * @param {number} params.grossEarnings   - total gross for this period
 * @param {number} params.otherDeductions - sum of manual deduction lines (loans, union fees...)
 *        NOT including tax/SI/GESY, which this function computes itself
 * @param {number} params.periodsPerYear  - 12 for monthly, 52 for weekly, etc.
 * @param {number} params.ytdInsurable    - SI-insurable earnings already used this year
 * @param {number} params.ytdGesyable     - GESY-liable earnings already used this year
 */
export function calculatePayslip({
  grossEarnings = 0,
  otherDeductions = 0,
  periodsPerYear = 12,
  ytdInsurable = 0,
  ytdGesyable = 0,
}) {
  const si = calculateSocialInsurance(grossEarnings, ytdInsurable);
  const gesy = calculateGESY(grossEarnings, ytdGesyable);
  const employerExtras = calculateEmployerExtras(grossEarnings, ytdInsurable);

  // SI + GESY are deducted before income tax is calculated
  const periodTaxable = Math.max(grossEarnings - si.employee - gesy.employee, 0);
  const annualTaxableEstimate = periodTaxable * periodsPerYear;
  const annualTax = calculateAnnualIncomeTax(annualTaxableEstimate);
  const periodTax = annualTax / periodsPerYear;

  const totalDeductions = periodTax + si.employee + gesy.employee + otherDeductions;
  const totalEmployerContributions = si.employer + gesy.employer + employerExtras.redundancyFund + employerExtras.humanResourceDevelopment + employerExtras.socialCohesionFund;
  const netPay = grossEarnings - totalDeductions;

  return {
    grossEarnings,
    incomeTax: round2(periodTax),
    socialInsurance: {
      employee: round2(si.employee),
      employer: round2(si.employer),
    },
    gesy: {
      employee: round2(gesy.employee),
      employer: round2(gesy.employer),
    },
    employerExtras: {
      redundancyFund: round2(employerExtras.redundancyFund),
      humanResourceDevelopment: round2(employerExtras.humanResourceDevelopment),
      socialCohesionFund: round2(employerExtras.socialCohesionFund),
    },
    otherDeductions: round2(otherDeductions),
    totalDeductions: round2(totalDeductions),
    totalEmployerContributions: round2(totalEmployerContributions),
    netPay: round2(netPay),
  };
}

function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}